import { createSelector } from '@reduxjs/toolkit';
import { SESSION_SLICE_NAME } from 'store/slices/slicesNames';
import { EMSANA_CARE } from 'utils/constants';
import * as selectClient from '../config/selectClient';

export const memberExtra = (state) => state[SESSION_SLICE_NAME].memberExtra;
export const zipcode = (state) => state[SESSION_SLICE_NAME].memberExtra?.zipcode;
export const accessToken = (state) => state[SESSION_SLICE_NAME].accessToken;
export const anonymousAnalyticsToken = (state) => state[SESSION_SLICE_NAME].anonymousAnalyticsToken;
export const isComplete = (state) => state[SESSION_SLICE_NAME].isComplete;
export const isLoading = (state) => state[SESSION_SLICE_NAME].isLoading;
export const isError = (state) => state[SESSION_SLICE_NAME].isError;
export const ssoRedirectUrl = (state) => state[SESSION_SLICE_NAME].ssoRedirectUrl;

export const hasAccessToken = createSelector([accessToken], (token) => Boolean(token));

/**
 * This selector validates the allowedOrigin and returns the origin if it is a valid URL accepted by the postMessage API.
 *
 * Invalid values such as localhost:3000 will return undefined.
 * The origin must contain a protocol to avoid crashes when calling postMessage
 */
export const embeddedTargetOrigin = createSelector(
  [(state) => state[SESSION_SLICE_NAME].memberExtra?.allowedOrigin],
  (allowedOrigin) => {
    try {
      const urlObj = new URL(allowedOrigin);

      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        console.error(
          'Invalid allowedOrigin',
          allowedOrigin,
          'Does not contain protocol http or https'
        );
        return undefined;
      }

      return urlObj.origin;
    } catch (_) {
      return undefined;
    }
  }
);

/**
 * This selector returns true if the user accessing the app is a Care Navigator. This value can be different per saml configuration.
 * For example, for Quantum, if the memberExtra assistedSearch property is set to true or yes, then the user is a Care Navigator.
 * For EMSANA Care, if the client name is EMSANA_CARE, then the user is a Care Navigator.
 */
export const isCareNavigator = createSelector(
  [memberExtra, selectClient.name],
  (memberExtraData, clientName) => {
    const assistedSearch =
      memberExtraData?.assistedSearch === 'yes' || memberExtraData?.assistedSearch === true;
    return assistedSearch || clientName === EMSANA_CARE;
  }
);

/**
 * This selector returns true if the user has an access token that is configured with customer support enabled.
 * If present and true, this should override the HIDE_SUPPORT feature flag when determining if the support link should be displayed.
 */
export const isCustomerSupportUser = createSelector(
  [memberExtra],
  (memberExtraData) => memberExtraData?.isCustomerSupportUser || false
);
